import { useState, useMemo } from 'react';
import { DonutChart } from '@/components/ui/Charts';
import { CHART_COLORS } from '@/components/ui/ChartLegend';

/**
 * SegmentsTable - Expandable table for consumer segments
 *
 * Pattern from previous version screenshots:
 * - Table with visual size bars
 * - Characteristics as tag chips
 * - Click row to expand detail panel
 * - Donut chart on right showing distribution
 */

export interface Segment {
  id: string;
  name: string;
  description?: string;
  share: number;
  growth: number;
  avgSpend?: number;
  priceIndex?: number;
  characteristics: string[];
  topCategories?: string[];
  population?: number;
  brand_loyalty?: number;
  price_sensitivity?: number;
}

interface SegmentsTableProps {
  segments: Segment[];
  currency?: string;
  language?: string;
  onSegmentSelect?: (segment: Segment) => void;
}

export function SegmentsTable({
  segments,
  currency = 'USD',
  language = 'en',
  onSegmentSelect,
}: SegmentsTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const t = language === 'es';

  // Colors for segments
  const colors = CHART_COLORS.categorical;

  // Prepare donut chart data
  const donutData = useMemo(() =>
    segments.map((seg, idx) => ({
      name: seg.name,
      value: seg.share * 100,
      color: colors[idx % colors.length],
    })),
    [segments]
  );

  const handleRowClick = (segment: Segment) => {
    setExpandedId(expandedId === segment.id ? null : segment.id);
    onSegmentSelect?.(segment);
  };

  const formatShare = (share: number) => `${(share * 100).toFixed(0)}%`;
  const formatSpend = (spend?: number) => {
    if (!spend) return '—';
    return `${currency} ${spend.toLocaleString()}`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Table Section */}
      <div className="lg:col-span-2">
        <div className="card overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-surface-600 bg-surface-800/50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-white">
                  {t ? 'Segmentos de Consumidores' : 'Consumer Segments'}
                </h3>
                <p className="text-xs text-text-tertiary">
                  {segments.length} {t ? 'segmentos identificados' : 'segments identified'}
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-text-tertiary uppercase tracking-wide border-b border-surface-600">
                  <th className="text-left py-3 px-4">{t ? 'Segmento' : 'Segment'}</th>
                  <th className="text-left py-3 px-4">{t ? 'Tamaño' : 'Size'}</th>
                  <th className="text-left py-3 px-4 hidden md:table-cell">{t ? 'Gasto Prom.' : 'Avg. Spend'}</th>
                  <th className="text-left py-3 px-4 hidden md:table-cell">{t ? 'Crecimiento' : 'Growth'}</th>
                  <th className="text-left py-3 px-4 hidden lg:table-cell">{t ? 'Características' : 'Characteristics'}</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((segment, idx) => {
                  const isExpanded = expandedId === segment.id;
                  const color = colors[idx % colors.length];

                  return (
                    <SegmentRow
                      key={segment.id}
                      segment={segment}
                      color={color}
                      isExpanded={isExpanded}
                      onClick={() => handleRowClick(segment)}
                      language={language}
                      currency={currency}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Donut Chart Section */}
      <div className="lg:col-span-1">
        <div className="card p-4">
          <h3 className="font-semibold text-white mb-4">
            {t ? 'Distribución de Segmentos' : 'Segment Distribution'}
          </h3>
          <div style={{ width: '100%', height: 200 }}>
            <DonutChart
              data={donutData}
              height={200}
              showLegend={false}
            />
          </div>
          {/* Legend */}
          <div className="mt-4 space-y-2">
            {segments.map((seg, idx) => (
              <div
                key={seg.id}
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-surface-700/50 rounded px-2 py-1 transition-colors"
                onClick={() => handleRowClick(seg)}
              >
                <span
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: colors[idx % colors.length] }}
                />
                <span className="text-text-secondary flex-1 truncate">{seg.name}</span>
                <span className="text-white font-medium">{formatShare(seg.share)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SegmentRow - Individual table row with expansion capability
 */
function SegmentRow({
  segment,
  color,
  isExpanded,
  onClick,
  language,
  currency,
}: {
  segment: Segment;
  color: string;
  isExpanded: boolean;
  onClick: () => void;
  language: string;
  currency: string;
}) {
  const t = language === 'es';

  const formatSpend = (spend?: number) => {
    if (!spend) return '—';
    return `${currency} ${spend.toLocaleString()}`;
  };

  const getPriceIndexLabel = (index?: number) => {
    if (!index) return '—';
    return `${index.toFixed(2)}x`;
  };

  const getPriceSensitivityLabel = (sens?: number) => {
    if (sens === undefined) return '—';
    if (sens < 0.3) return t ? 'Baja' : 'Low';
    if (sens < 0.6) return t ? 'Media' : 'Medium';
    return t ? 'Alta' : 'High';
  };

  const getLoyaltyLabel = (loyalty?: number) => {
    if (loyalty === undefined) return '—';
    return `${(loyalty * 100).toFixed(0)}%`;
  };

  return (
    <>
      <tr
        className={`
          cursor-pointer transition-colors border-b border-surface-600/50
          ${isExpanded ? 'bg-surface-700/50' : 'hover:bg-surface-700/30'}
        `}
        onClick={onClick}
      >
        {/* Segment Name */}
        <td className="py-3 px-4">
          <div className="flex items-center gap-3">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: color }}
            />
            <div className="min-w-0">
              <div className="font-medium text-white truncate">{segment.name}</div>
              {segment.description && (
                <div className="text-xs text-text-tertiary truncate max-w-[200px]">
                  {segment.description}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Size Bar */}
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-surface-600 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(segment.share * 100, 100)}%`,
                  backgroundColor: color,
                }}
              />
            </div>
            <span className="text-sm text-white font-medium min-w-[40px]">
              {(segment.share * 100).toFixed(0)}%
            </span>
          </div>
        </td>

        {/* Avg Spend */}
        <td className="py-3 px-4 hidden md:table-cell">
          <span className="text-sm text-white">{formatSpend(segment.avgSpend)}</span>
        </td>

        {/* Growth */}
        <td className="py-3 px-4 hidden md:table-cell">
          <span className={`text-sm font-medium ${
            segment.growth >= 0 ? 'text-success-400' : 'text-danger-400'
          }`}>
            {segment.growth >= 0 ? '+' : ''}{(segment.growth * 100).toFixed(1)}%
          </span>
        </td>

        {/* Characteristics */}
        <td className="py-3 px-4 hidden lg:table-cell">
          <div className="flex gap-1 flex-wrap">
            {segment.characteristics.slice(0, 2).map((char, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-surface-600 text-text-secondary text-xs rounded-full"
              >
                {char}
              </span>
            ))}
            {segment.characteristics.length > 2 && (
              <span className="text-xs text-text-tertiary">
                +{segment.characteristics.length - 2}
              </span>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded Detail Row */}
      {isExpanded && (
        <tr>
          <td colSpan={5} className="p-0">
            <SegmentDetailPanel
              segment={segment}
              color={color}
              language={language}
              currency={currency}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * SegmentDetailPanel - Expanded detail view for a segment
 */
function SegmentDetailPanel({
  segment,
  color,
  language,
  currency,
}: {
  segment: Segment;
  color: string;
  language: string;
  currency: string;
}) {
  const t = language === 'es';

  const formatSpend = (spend?: number) => {
    if (!spend) return '—';
    return `${currency} ${spend.toLocaleString()}`;
  };

  const getPriceIndexLabel = (index?: number) => {
    if (!index) return '—';
    return `${index.toFixed(2)}x`;
  };

  const getPriceSensitivityLabel = (sens?: number) => {
    if (sens === undefined) return '—';
    if (sens < 0.3) return { label: t ? 'Baja' : 'Low', color: 'text-success-400' };
    if (sens < 0.6) return { label: t ? 'Media' : 'Medium', color: 'text-warning-400' };
    return { label: t ? 'Alta' : 'High', color: 'text-danger-400' };
  };

  const getLoyaltyLabel = (loyalty?: number) => {
    if (loyalty === undefined) return { label: '—', color: 'text-text-tertiary' };
    const pct = (loyalty * 100).toFixed(0);
    if (loyalty >= 0.7) return { label: `${pct}%`, color: 'text-success-400' };
    if (loyalty >= 0.4) return { label: `${pct}%`, color: 'text-warning-400' };
    return { label: `${pct}%`, color: 'text-danger-400' };
  };

  const formatPopulation = (pop?: number) => {
    if (!pop) return '—';
    if (pop >= 1_000_000) return `${(pop / 1_000_000).toFixed(1)}M`;
    if (pop >= 1_000) return `${(pop / 1_000).toFixed(0)}K`;
    return pop.toString();
  };

  const priceSens = getPriceSensitivityLabel(segment.price_sensitivity);
  const loyalty = getLoyaltyLabel(segment.brand_loyalty);

  return (
    <div className="bg-surface-800/80 border-t border-b border-surface-600 p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <span
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: color }}
        />
        <h3 className="font-semibold text-white">{segment.name}</h3>
        <span className="px-2 py-0.5 bg-primary-500/20 text-primary-400 text-xs rounded-full">
          {(segment.share * 100).toFixed(0)}% {t ? 'del mercado' : 'of market'}
        </span>
      </div>

      {segment.description && (
        <p className="text-sm text-text-secondary mb-4">{segment.description}</p>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Avg Spend */}
        <div>
          <div className="text-xs text-text-tertiary uppercase mb-1">
            {t ? 'Gasto Promedio' : 'Avg. Spend'}
          </div>
          <div className="text-lg font-semibold text-white">
            {formatSpend(segment.avgSpend)}
          </div>
        </div>

        {/* Price Index */}
        <div>
          <div className="text-xs text-text-tertiary uppercase mb-1">
            {t ? 'Índice de Precio' : 'Price Index'}
          </div>
          <div className="text-lg font-semibold text-primary-400">
            {getPriceIndexLabel(segment.priceIndex)}
          </div>
        </div>

        {/* Brand Loyalty */}
        <div>
          <div className="text-xs text-text-tertiary uppercase mb-1">
            {t ? 'Lealtad de Marca' : 'Brand Loyalty'}
          </div>
          <div className={`text-lg font-semibold ${loyalty.color}`}>
            {loyalty.label}
          </div>
        </div>

        {/* Price Sensitivity */}
        <div>
          <div className="text-xs text-text-tertiary uppercase mb-1">
            {t ? 'Sensibilidad al Precio' : 'Price Sensitivity'}
          </div>
          <div className={`text-lg font-semibold ${typeof priceSens === 'object' ? priceSens.color : 'text-white'}`}>
            {typeof priceSens === 'object' ? priceSens.label : priceSens}
          </div>
        </div>
      </div>

      {/* Characteristics & Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-surface-600">
        {/* Top Categories */}
        {segment.topCategories && segment.topCategories.length > 0 && (
          <div>
            <div className="text-xs text-text-tertiary uppercase mb-2">
              {t ? 'Top Categorías' : 'Top Categories'}
            </div>
            <div className="flex flex-wrap gap-1">
              {segment.topCategories.map((cat, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-surface-700 text-text-secondary text-xs rounded"
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Characteristics */}
        <div>
          <div className="text-xs text-text-tertiary uppercase mb-2">
            {t ? 'Características' : 'Characteristics'}
          </div>
          <div className="flex flex-wrap gap-1">
            {segment.characteristics.map((char, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded"
              >
                {char}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Population */}
      {segment.population && (
        <div className="mt-4 pt-4 border-t border-surface-600 text-sm text-text-tertiary">
          {t ? 'Población estimada' : 'Estimated population'}: {' '}
          <span className="text-white font-medium">{formatPopulation(segment.population)}</span>
          {' '}{t ? 'consumidores' : 'consumers'}
        </div>
      )}
    </div>
  );
}
